/**
 * 错误边界
 * ---------------------------------------------------------------------------
 * React 里渲染期抛出的异常如果没人接，整棵树会被卸载，用户看到的是纯白页面。
 * 收银台白屏的后果不是"体验不好"，是门店以为系统崩了、开始打电话，
 * 而实际上可能只是某一块的数据形状没对上。
 *
 * 这里必须是 class 组件：componentDidCatch / getDerivedStateFromError
 * 目前没有等价的 Hook。
 *
 * 两类错误分开处理：
 *
 * 1. 分块加载失败（ChunkLoadError / 动态 import 失败）
 *    按路由做了代码分包之后，切页面要现拉一个 JS 分块。门店 WiFi 抖一下，
 *    或者刚发过版、浏览器还拿着旧的 index.html 去要已经被替换掉的旧分块，
 *    都会失败。这种错误刷新页面就能好，所以单独识别出来，给"重新加载"。
 *
 * 2. 其他渲染异常
 *    给一个能回到上一页/首页的出口，并把错误信息原样显示出来——
 *    门店把这段话报给客服，比"它坏了"有用得多。
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { isChunkLoadError } from '@/app/chunk-load-error';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/icons';

type Props = {
  children: ReactNode;
  /**
   * 变化时清掉错误状态，让下一页正常渲染。传当前路径即可。
   *
   * **不要改用 `key={路径}` 的写法。** 那样确实能复位，但代价是
   * 每次切页整棵子树被卸载重建——侧栏跟着重新挂载，
   * 滚动位置回到顶部，点最下面那几个菜单时会看到侧栏"往上顶"一下。
   * 而且整个外壳白重建一遍，切页也更慢。
   */
  resetKey?: string;
};
type State = { error: Error | null };

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidUpdate(previous: Props): void {
    // 只在真的出过错时复位，避免每次导航都白 setState 一次
    if (this.state.error && previous.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // 生产环境接入前端监控时，这里是唯一的上报入口。
    // 现在只打到控制台：没有真实上报通道就不要假装已经上报了。
    console.error('[ErrorBoundary]', error, info.componentStack);
  }

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    const chunkFailed = isChunkLoadError(error);

    return (
      <div className="grid h-full place-items-center px-6 py-14">
        <div className="max-w-[42ch] text-center">
          <span className="mx-auto grid size-11 place-items-center rounded-tile bg-red-pale text-red">
            <Icon name="alert" size={20} />
          </span>
          <p className="mt-3 text-card-title font-bold tracking-title">
            {chunkFailed ? '这一页没能加载出来' : '这一页出错了'}
          </p>
          <p className="mt-1 text-note leading-relaxed text-ink-3">
            {chunkFailed
              ? '可能是网络断了一下，也可能是系统刚更新过。重新加载一次通常就好了。'
              : '已经记录下来了。如果反复出现，把下面这行信息报给客服。'}
          </p>

          {!chunkFailed && (
            <pre className="scroll-thin mt-3 max-h-[120px] overflow-auto rounded-md border border-line bg-panel-2 px-3 py-2 text-left text-tiny break-words whitespace-pre-wrap text-ink-2">
              {error.name}: {error.message}
            </pre>
          )}

          <div className="mt-4 flex justify-center gap-2">
            <Button
              variant="primary"
              icon="refresh"
              onClick={() => window.location.reload()}
            >
              重新加载
            </Button>
            {!chunkFailed && (
              <Button
                onClick={() => {
                  // 清掉错误状态再退回上一页，避免又渲染出同一个坏页面
                  this.setState({ error: null });
                  window.history.back();
                }}
              >
                返回上一页
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }
}
